<script setup>
/**
 * One of the user's OWN cards in global search (the `inventory` block, CDP-2508).
 *
 * Reads as a card row, same anatomy as the Card Database result beside it:
 *
 *   [thumb]  #123 Player Name              [View in Inventory] [View in Batch]
 *            2024 Panini Prizm
 *            (Parallel) (Variation) (Attribute)
 *
 * Actions are "go and look at it", not "add it" — they already own it:
 *   one batch    → View in Inventory · View in Batch
 *   many batches → View in Inventory · In N batches ⌄
 *
 * "Many batches" means the same card owned more than once. The API returns each
 * copy as its own hit with no id tying them together, so the parent groups them
 * and passes the whole group here — see `groupInventoryHits`.
 */
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import AppButton from '@/components/atoms/AppButton'
import SearchCardBatchesMenu from './SearchCardBatchesMenu.vue'
import { useSearchStore } from '@/stores/SearchStore'
import { batchesForHits, inventoryBatchCount, inventoryCardTitle, inventoryCardSubtitle, inventoryCardImages } from '@/helpers/globalSearch'
import { batchViewPath } from '@/helpers/batch'

const props = defineProps({
  active: { type: Boolean, default: false },
  /** `{ key, card, items }` from `groupInventoryHits`. */
  group: { type: Object, required: true },
})

const router = useRouter()
const searchStore = useSearchStore()

const card = computed(() => props.group.card || {})

const title = computed(() => inventoryCardTitle(card.value))
const subtitle = computed(() => inventoryCardSubtitle(card.value))
const images = computed(() => inventoryCardImages(card.value))

/** Parallel · Variation · Attribute, in the design's order. Empties dropped. */
const tags = computed(() => [card.value.subset, card.value.variation, card.value.attributes || card.value.flags]
  .filter(Boolean))

// Batch names come off the hits themselves — no /batches request.
const batches = computed(() => batchesForHits(props.group.items))

const batchCount = computed(() => inventoryBatchCount(card.value, batches.value.length))

const closeSearch = () => { searchStore.cdp_search.open = false }

const goToInventory = () => {
  if (!card.value.id) return

  closeSearch()
  router.push(`/inventory/${card.value.id}`)
}

// Design-aware: `/inventory/batch/:id/...` in the new design, `/batches/:id/...`
// in the classic one.
const goToBatch = (batch) => {
  if (!batch?.id) return

  closeSearch()
  router.push(batchViewPath(batch.id, { cardId: batch.cardId }))
}
</script>

<template>
  <li
    class="flex items-center gap-x-2 p-3 transition duration-300 hover:bg-blue-50 group"
    :class="{ 'bg-blue-50': props.active }"
  >
    <!-- Same 40px slot the database row uses, so the two sections line up.
         Renders the fallback until the API carries card images. -->
    <div class="w-10 h-10 relative flex-none flex items-center justify-center">
      <img :src="images.front" class="relative z-10 max-h-10" />
      <img v-if="images.back" :src="images.back" class="absolute top-1/2 -translate-y-1/2" />
    </div>

    <div class="flex flex-col flex-grow min-w-0">
      <h3 class="font-medium mb-0.5 truncate">{{ title }}</h3>
      <p class="text-gray-500 mb-1.5 truncate">{{ subtitle }}</p>

      <div class="flex items-center flex-wrap gap-1 text-[10px] mt-auto">
        <span
          v-for="tag in tags"
          :key="tag"
          class="badge secondary border border-white px-1.5 rounded-full"
        >{{ tag }}</span>
      </div>
    </div>

    <!-- `.stop.prevent` throughout: the row is a ComboboxOption, and a click
         reaching it would select the option instead of navigating. -->
    <div
      class="flex items-center gap-x-2 flex-none lg:opacity-0 group-hover:opacity-100 transition duration-300"
      :class="{ '!opacity-100': props.active }"
      @mousedown.stop.prevent
      @click.stop.prevent
    >
      <AppButton variant="outlined" size="sm" @click.stop.prevent="goToInventory">
        View in Inventory
      </AppButton>

      <SearchCardBatchesMenu
        v-if="batchCount > 1"
        :batches="batches"
        :count="batchCount"
        @select="goToBatch"
      />
      <AppButton
        v-else-if="batches.length === 1"
        variant="outlined"
        size="sm"
        @click.stop.prevent="goToBatch(batches[0])"
      >
        View in Batch
      </AppButton>
    </div>
  </li>
</template>
